import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Table2, BarChart3, PieChart as PieIcon, LineChart as LineIcon } from 'lucide-react';

const COLORS = ['#1E88E5', '#00C896', '#FFA000', '#A78BFA', '#EF4444', '#2DD4BF', '#F472B6', '#94A3B8', '#38BDF8', '#FB923C'];
const VIEWS = [['table', 'Table', Table2], ['bar', 'Bar', BarChart3], ['pie', 'Pie', PieIcon], ['line', 'Line', LineIcon]];

const NUM_RE = /^[-+]?\$?\s*[\d,]*\.?\d+\s*(h|hrs|%)?$/i;
const num = (v) => {
  if (typeof v === 'number') return v;
  if (v == null || typeof v !== 'string' || !NUM_RE.test(v.trim())) return NaN;
  const n = parseFloat(v.replace(/[$,%\s]|hrs|h/gi, ''));
  return Number.isNaN(n) ? NaN : n;
};

// Detect which columns are usable as labels vs numeric values from the data itself.
export function chartColumns(rows, columns) {
  const sample = rows.slice(0, 50);
  const numeric = [], labels = [];
  columns.forEach((c) => {
    const vals = sample.map((r) => r[c.key]).filter((v) => v != null && v !== '');
    if (!vals.length) return;
    const nums = vals.filter((v) => !Number.isNaN(num(v)) && typeof v !== 'boolean');
    if (nums.length / vals.length > 0.8 && !/id$/i.test(c.key)) numeric.push(c);
    else if (typeof vals[0] === 'string' && !/id$/i.test(c.key)) labels.push(c);
  });
  return { numeric, labels };
}

// Table / Bar / Pie / Line switcher — auto-built from the report's own columns.
// Renders only the controls + chart; the caller decides whether to show the table.
export default function ChartSwitcher({ rows, columns, view, onView, chartRef }) {
  const { numeric, labels } = useMemo(() => chartColumns(rows, columns), [rows, columns]);
  const [labelKey, setLabelKey] = useState('');
  const [valueKey, setValueKey] = useState('');
  const lk = labels.some((c) => c.key === labelKey) ? labelKey : labels[0]?.key;
  const vk = numeric.some((c) => c.key === valueKey) ? valueKey : numeric[0]?.key;

  const data = useMemo(() => {
    if (!lk || !vk) return [];
    const agg = new Map();
    rows.forEach((r) => {
      const k = String(r[lk] ?? '—');
      const v = num(r[vk]);
      if (Number.isNaN(v)) return;
      agg.set(k, (agg.get(k) || 0) + v);
    });
    const out = [...agg.entries()].map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));
    return view === 'line' ? out : out.sort((a, b) => b.value - a.value).slice(0, 15);
  }, [rows, lk, vk, view]);

  const canChart = numeric.length > 0 && labels.length > 0;
  const vLabel = numeric.find((c) => c.key === vk)?.label || vk;

  return (
    <div data-testid="chart-switcher">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #2D3748' }} data-testid="chart-view-toggle">
          {VIEWS.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => onView(k)} disabled={k !== 'table' && !canChart}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium disabled:opacity-40"
              style={{ background: view === k ? '#1E88E5' : 'transparent', color: view === k ? '#fff' : '#A0AEC0' }}
              data-testid={`chart-view-${k}`} title={k !== 'table' && !canChart ? 'This report has no numeric column to chart' : ''}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        {view !== 'table' && canChart && (
          <>
            <select value={lk} onChange={(e) => setLabelKey(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#E2E8F0', border: '1px solid #2D3748' }} data-testid="chart-label-select">
              {labels.map((c) => <option key={c.key} value={c.key}>Group: {c.label}</option>)}
            </select>
            <select value={vk} onChange={(e) => setValueKey(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#E2E8F0', border: '1px solid #2D3748' }} data-testid="chart-value-select">
              {numeric.map((c) => <option key={c.key} value={c.key}>Value: {c.label}</option>)}
            </select>
            <span className="text-[11px]" style={{ color: '#64748B' }}>{view === 'line' ? 'in row order' : 'top 15'}</span>
          </>
        )}
      </div>
      {view !== 'table' && canChart && (
        <div ref={chartRef} className="rounded-xl p-3 mb-3" style={{ background: '#111C2C', border: '1px solid #2D3748', height: 340 }} data-testid="chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            {view === 'pie' ? (
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" outerRadius={120} label={({ name, percent }) => `${name.length > 18 ? `${name.slice(0, 18)}…` : name} ${(percent * 100).toFixed(0)}%`}>
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', color: '#fff' }} />
                <Legend />
              </PieChart>
            ) : view === 'line' ? (
              <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid stroke="#2D3748" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: '#A0AEC0', fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fill: '#A0AEC0', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', color: '#fff' }} />
                <Line type="monotone" dataKey="value" name={vLabel} stroke="#1E88E5" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            ) : (
              <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid stroke="#2D3748" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#A0AEC0', fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fill: '#A0AEC0', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', color: '#fff' }} />
                <Bar dataKey="value" name={vLabel} radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
